import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AuditAction } from '../../lib/api/audits';
import { auditsAPI } from '../../lib/api/audits';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useView } from '../../contexts/ViewContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuditsData } from './useAuditsData';
import AuditKpiTiles from './AuditKpiTiles';
import AuditsTable from './AuditsTable';
import ActionsTable from './ActionsTable';
import AuditDialog from './AuditDialog';
import ActionDialog from './ActionDialog';

type Tab = 'audits' | 'actions';

/**
 * Audits — every supplier audit and action in the caller's scope.
 * Customer users plan audits and raise, edit and close actions; supplier
 * users see the audits raised on them and their actions, read-only.
 */
export default function AuditsPage() {
  const { portalMode } = useView();
  const location = useLocation();
  const navigate = useNavigate();
  const readOnly = portalMode === 'supplier';
  const tab: Tab = location.pathname.endsWith('/actions') ? 'actions' : 'audits';
  const { audits, actions, summary, loading, error, reload } = useAuditsData();
  const [planOpen, setPlanOpen] = useState(false);
  const [actionOpen, setActionOpen] = useState(false);
  const [editingAction, setEditingAction] = useState<AuditAction | null>(null);

  const completeAction = async (action: AuditAction) => {
    try {
      await auditsAPI.completeAction(action.id);
      toast.success(`"${action.title}" completed`);
      reload();
    } catch (err: unknown) {
      console.error('Complete action failed:', err);
      toast.error(getErrorMessage(err, 'Could not complete the action'));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Audits</h1>
        <p className="mt-1 max-w-3xl text-muted-foreground">
          {readOnly
            ? 'The audits your customer has raised on your company, and the actions that came out of them.'
            : 'Plan supplier audits, record the outcome, and track the actions raised — every date lands on the Calendar and overdue actions raise a notification.'}
        </p>
      </div>

      <AuditKpiTiles summary={summary} loading={loading} />

      <Tabs value={tab} onValueChange={(v) => navigate(v === 'actions' ? '/audits/actions' : '/audits')}>
        <TabsList data-testid="audits-tabs">
          <TabsTrigger value="audits">Audits ({audits.length})</TabsTrigger>
          <TabsTrigger value="actions">Actions ({actions.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {error ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <AlertCircle className="h-10 w-10 text-red-500" />
            <p className="text-sm font-medium">Could not load audits</p>
            <p className="max-w-md text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={reload}><RefreshCw className="mr-2 h-4 w-4" />Retry</Button>
          </CardContent>
        </Card>
      ) : tab === 'audits' ? (
        <AuditsTable audits={audits} loading={loading} readOnly={readOnly} onPlan={() => setPlanOpen(true)} />
      ) : (
        <ActionsTable
          actions={actions} loading={loading} readOnly={readOnly}
          onRaise={() => { setEditingAction(null); setActionOpen(true); }}
          onEdit={(a) => { setEditingAction(a); setActionOpen(true); }}
          onComplete={completeAction}
        />
      )}

      {!readOnly && (
        <>
          <AuditDialog open={planOpen} onClose={() => setPlanOpen(false)} onSaved={(a) => navigate(`/audits/${a.id}`)} />
          <ActionDialog open={actionOpen} onClose={() => setActionOpen(false)} action={editingAction} onSaved={reload} />
        </>
      )}
    </div>
  );
}
